import type { SupportedLanguage } from '@zag/core/common';

/**
 * Generic render hooks that other modules (timetracking, external calendar sync,
 * planning, etc.) can supply to the calendar render layer to customise how a
 * specific kind of slot is displayed without the calendar module needing to
 * know anything about that kind.
 *
 * Each hook receives the raw slot `data` payload (the JSONB blob stored on
 * `calendar_slots.data`) and returns either a value or `undefined`. Returning
 * `undefined` means "I don't know how to render this slot — fall back to the
 * default behaviour or the next hook in the chain". Composing multiple hooks
 * follows a first-non-undefined-wins strategy (see {@link composeSlotRenderHooks}).
 *
 * The calendar render layer (`mapCalendarSlots`) reads from explicit `data`
 * fields first and falls back to the hook output only when those are absent.
 * This means hook output never overrides values a slot author set explicitly.
 */
export interface SlotRenderHooks {
  /**
   * Resolve a display title for a slot when neither `data.text` nor
   * `data.translations[lang].text` is set.
   *
   * Used by modules that derive titles from other slot fields (e.g. an absence
   * module deriving the title from `data.absenceType`, or an external sync
   * module deriving it from `data.sourceSubject`).
   */
  readonly resolveTitle?: (raw: Record<string, unknown>, language?: SupportedLanguage) => string | undefined;

  /**
   * Resolve a fallback icon (Material Icons key) when `data.icon` is missing.
   */
  readonly resolveIcon?: (raw: Record<string, unknown>) => string | undefined;

  /**
   * Resolve a fallback colour when neither the per-calendar override nor
   * `data.color` are set. Receives the raw slot data only — the caller still
   * applies its own final fallback (e.g. the deterministic per-calendar hash).
   */
  readonly resolveColor?: (raw: Record<string, unknown>) => string | undefined;

  /**
   * Resolve a fallback `isAllDay` flag when `data.isAllDay` is missing.
   * Return `true` for slot kinds that should always span the whole day.
   */
  readonly resolveIsAllDay?: (raw: Record<string, unknown>) => boolean | undefined;

  /**
   * Allow a slot to remain editable even when its host calendar is read-only.
   * Used e.g. for personal absences that should always be editable by their
   * owner regardless of calendar-level permissions. Defaults to `false` when
   * the hook is not provided.
   */
  readonly isEditableOverride?: (raw: Record<string, unknown>) => boolean;

  /**
   * Return the set of form field names that should be hidden in the slot editor
   * for a given slot type key. Returning `undefined` means "show all fields
   * (I don't know about this slot type)".
   *
   * Known field names: `'title'`, `'description'`, `'locationOverride'`.
   *
   * Used by editor components to make the form generic — external modules
   * (timetracking, external sync, etc.) declare which fields are irrelevant
   * for their slot types without the calendar editor knowing about them.
   */
  readonly resolveHiddenFormFields?: (slotTypeKey: string, category?: string) => ReadonlyArray<string> | undefined;
}

/**
 * Compose multiple {@link SlotRenderHooks} into a single hook bundle.
 *
 * For each hook field, the composed hook walks the provided hooks in order and
 * returns the first non-`undefined` value. Falsy values that are not
 * `undefined` (e.g. `false` for `resolveIsAllDay`) are preserved.
 *
 * @param hooks Render hook bundles to compose. Earlier hooks take precedence.
 */
export function composeSlotRenderHooks(...hooks: ReadonlyArray<SlotRenderHooks | undefined | null>): SlotRenderHooks {
  const active = hooks.filter((h): h is SlotRenderHooks => h != null);
  if (active.length === 0) return {};
  if (active.length === 1) return active[0];

  return {
    resolveTitle: (raw, language) => firstDefined(active, h => h.resolveTitle?.(raw, language)),
    resolveIcon: raw => firstDefined(active, h => h.resolveIcon?.(raw)),
    resolveColor: raw => firstDefined(active, h => h.resolveColor?.(raw)),
    resolveIsAllDay: raw => firstDefined(active, h => h.resolveIsAllDay?.(raw)),
    isEditableOverride: raw => active.some(h => h.isEditableOverride?.(raw) === true),
    resolveHiddenFormFields: (key, category) => firstDefined(active, h => h.resolveHiddenFormFields?.(key, category)),
  };
}

function firstDefined<T>(hooks: ReadonlyArray<SlotRenderHooks>, pick: (h: SlotRenderHooks) => T | undefined): T | undefined {
  for (const h of hooks) {
    const value = pick(h);
    if (value !== undefined) return value;
  }
  return undefined;
}
